import { useEffect, useRef, useState } from 'preact/hooks';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import styleSheet from '../styles/shareButton.module.scss';
import ShareIcon from '../../assets/icons/ShareIcon';

const styles = styleSheet.locals || {};
const COPIED_HOLD_MS = 3000;

/**
 * Copies a composed message to the clipboard and briefly confirms it. Styled to
 * match the Grid Guesser share button.
 *
 * @param {object} props
 * @param {string} props.message - Text copied to the clipboard.
 * @param {Function} [props.onShare] - Called after a successful copy.
 * @returns {Function} <ShareButton />
 */
function ShareButton({ message, onShare }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(
        () => () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        },
        []
    );

    const handleClick = () => {
        navigator.clipboard.writeText(message).then(() => {
            setCopied(true);
            onShare?.();
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(
                () => setCopied(false),
                COPIED_HOLD_MS
            );
        });
    };

    return (
        <>
            <style>{styleSheet.toString()}</style>
            <button
                type="button"
                className={styles.shareButton}
                onClick={handleClick}
                aria-live="polite"
                data-testid="share-btn"
            >
                <span
                    key={copied ? 'copied' : 'idle'}
                    className={styles.shareButtonLabel}
                >
                    {copied
                        ? t('selector.teamCopied')
                        : t('selector.shareYourTeam')}
                </span>
                <ShareIcon className={styles.shareButtonIcon} />
            </button>
        </>
    );
}

ShareButton.propTypes = {
    message: PropTypes.string.isRequired,
    onShare: PropTypes.func
};

export default ShareButton;
